import {
  useQuery,
  useMutation,
  queryOptions,
  type UseQueryResult,
  type UseMutationResult,
} from "@tanstack/react-query";

import type {
  V3UserInfo,
  V3User,
  V3UserUpdateFields,
  UserNotificationPreferences,
  UserNotificationPreferencesUpdateFields,
  V2UserUpdatePasswordFields,
} from "@typings/mightierApiTypes";

import {
  accountActivationBackendApi,
  backendApi,
} from "src/backendApi/backendApi";
import { useAccountActivationAuthStore } from "../accountActivation/authStore";

type ApiClientThatCanFetchUserInfo =
  | typeof accountActivationBackendApi
  | typeof backendApi;

const makeGetUserInfoFn = (
  client: ApiClientThatCanFetchUserInfo,
): (() => Promise<V3UserInfo>) => {
  return async () => {
    const resp = await client.getUserInfo();
    return resp.data;
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const userInfoQueryOpts = () => {
  return queryOptions({
    queryKey: ["USER_INFO"],
    queryFn: makeGetUserInfoFn(backendApi),
  });
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const retrieveUserQueryOpts = (userId: number) => {
  return queryOptions({
    queryKey: ["users", "retrieve", userId],
    queryFn: async () => {
      const resp = await backendApi.retrieveUser(userId);
      return resp.data;
    },
  });
};

/**
 * NOTE: this should ONLY BE USED on the account activation apge!!
 */
export const useUserInfoQueryWithOauth2TokenOnlyForAccountActivationPage =
  (): UseQueryResult<V3UserInfo> => {
    // since this query doesn't have any dependencies (i.e. it's the "root" of rest API
    // so doesnt require any IDs/anthing) we want to prevent it from firing too early
    const oauth2Token = useAccountActivationAuthStore().oauth2Token;

    return useQuery({
      queryKey: ["USER_INFO_VIA_OAUTH_TOKEN"],
      queryFn: makeGetUserInfoFn(accountActivationBackendApi),
      // zustand store will re-render when backendApi updates oauth2 token
      enabled: typeof oauth2Token === "string",
    });
  };

/**
 * MUTATIONS
 */

export const usePatchUserMutation = (
  userId: number | undefined,
): UseMutationResult<V3User, unknown, V3UserUpdateFields> => {
  return useMutation({
    mutationKey: ["PATCH_USER", userId],
    mutationFn: async (data: V3UserUpdateFields) => {
      if (userId === undefined) {
        throw new Error("Attempted to update user without an ID");
      }
      const resp = await accountActivationBackendApi.patchUser(userId, data);
      return resp.data;
    },
  });
};

export const usePatchUserNotificationPreferencesMutation = (
  userId: number | undefined,
): UseMutationResult<
  UserNotificationPreferences,
  unknown,
  UserNotificationPreferencesUpdateFields
> => {
  return useMutation({
    mutationKey: ["PATCH_USER_NOTIFICATION_PREFERENCES", userId],
    mutationFn: async (data: UserNotificationPreferencesUpdateFields) => {
      if (userId === undefined) {
        throw new Error(
          "Attempted to patch user notification_preferences without an ID",
        );
      }
      const resp =
        await accountActivationBackendApi.patchUserNotificationPreferences(
          userId,
          data,
        );
      return resp.data;
    },
  });
};

export const useUserSetPasswordMutation = (): UseMutationResult<
  unknown,
  unknown,
  V2UserUpdatePasswordFields
> => {
  return useMutation({
    mutationKey: ["USER_SET_PASSWORD"],
    mutationFn: async (variables) => {
      const resp = await accountActivationBackendApi.setPasswordForLoggedInUser(
        variables.password,
      );
      return resp.data;
    },
  });
};
